/**
 * WelcomeCard Component
 * Displays the main welcome section with dimensions and challenge info
 */
import React from 'react';
import { DIMENSIONS, CHALLENGE_INFO } from '../../types/dashboard';

interface WelcomeCardProps {
  remainingAttempts: number;
  onStartClick: () => void;
}

export const WelcomeCard: React.FC<WelcomeCardProps> = ({ 
  remainingAttempts, 
  onStartClick 
}) => {
  return (
    <div className="bg-white rounded-xl shadow-lg p-6 mb-4">
      <h2 className="text-2xl font-bold text-primary text-center mb-1">
        Bienvenue !
      </h2>
      <h1 className="text-2xl font-bold text-primary text-center mb-2">
        SKILLSPRINT CHALLENGE
      </h1>
      
      <p className="text-gray-600 text-center mb-6">
        Évaluez vos capacités d'apprentissage en 5 dimensions
      </p>
      
      {/* Dimensions Grid */}
      <DimensionsGrid />

      {/* Challenge Info */}
      <ChallengeInfoBanner />

      {/* What you need */}
      <WhatYouNeedSection />

      {/* Action */}
      <ActionSection 
        remainingAttempts={remainingAttempts} 
        onStartClick={onStartClick} 
      />
    </div>
  );
};

// Dimensions grid sub-component
const DimensionsGrid: React.FC = () => (
  <div className="mb-6">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">Les 5 dimensions évaluées :</h3>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
      {DIMENSIONS.map((dim, idx) => (
        <div 
          key={idx} 
          className="bg-gradient-to-r from-primary/5 to-primary/10 p-4 rounded-lg border-l-4 border-primary"
        >
          <p className="font-semibold text-gray-800 text-sm mb-1">{dim.name}</p>
          <p className="text-gray-600 text-xs">{dim.desc}</p>
        </div>
      ))}
    </div>
  </div>
);

// Challenge info banner sub-component
const ChallengeInfoBanner: React.FC = () => (
  <div className="bg-secondary bg-opacity-10 p-5 rounded-lg mb-6">
    <div className="grid grid-cols-3 gap-4 max-w-md mx-auto">
      <InfoItem value={CHALLENGE_INFO.questions.toString()} label="questions" />
      <InfoItem value={CHALLENGE_INFO.duration} label="durée" />
      <InfoItem value={`${CHALLENGE_INFO.maxScore} pts`} label="score maximum" />
    </div>
  </div>
);

const InfoItem: React.FC<{ value: string; label: string }> = ({ value, label }) => (
  <div className="text-center">
    <p className="text-2xl font-bold text-secondary">{value}</p>
    <p className="text-xs text-gray-600 mt-1">{label}</p>
  </div>
);

// What you need section sub-component
const WhatYouNeedSection: React.FC = () => (
  <div className="bg-gray-50 rounded-lg p-4 mb-6">
    <h3 className="text-sm font-semibold text-gray-700 mb-3">Ce dont vous avez besoin :</h3>
    <div className="space-y-2">
      <NeedItem text="45 minutes de temps libre" />
      <NeedItem text="Papier et stylo pour les calculs" />
      <NeedItem text="Un environnement calme" />
    </div>
  </div>
);

const NeedItem: React.FC<{ text: string }> = ({ text }) => (
  <div className="flex items-center gap-3">
    <div className="w-2 h-2 bg-secondary rounded-full flex-shrink-0"></div>
    <p className="text-sm text-gray-700">{text}</p>
  </div>
);

// Action section sub-component
interface ActionSectionProps {
  remainingAttempts: number;
  onStartClick: () => void;
}

const ActionSection: React.FC<ActionSectionProps> = ({ remainingAttempts, onStartClick }) => (
  <div className="border-t pt-5">
    <div className="text-center mb-4">
      <p className="text-gray-600 text-sm mb-1">Tentatives restantes</p>
      <p className="font-bold text-primary text-3xl">{remainingAttempts}</p>
    </div>
    
    <button
      onClick={onStartClick}
      disabled={remainingAttempts === 0}
      className="w-full bg-secondary hover:bg-opacity-90 text-white font-bold py-3 rounded-lg text-base transition disabled:opacity-50 disabled:cursor-not-allowed"
    >
      Commencer le test
    </button>

    {remainingAttempts === 0 && (
      <p className="text-red-600 text-sm mt-3 text-center font-medium">
        Vous avez utilisé toutes vos tentatives
      </p>
    )}
  </div>
);

export default WelcomeCard;